import React, {useEffect, useState} from 'react';
import {
  getAnonymousRequestToken, getLaunchPayload,
} from '../../../state/guest-thunk';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {useParams} from 'react-router';
import {paynowService} from '../../../services/paynow-service';
import {
  PatientEncounter,
} from '../../../../patient/components/models/patient-encounter';
import {
  ApplicationStatusState,
  emptyApplicationStatus, GetError,
} from '../../../../security/model/application-status';
import {logUserOut} from '../../../../security/state/user-thunk';
import {GuestToken} from '../../../../security/services/SSO';
import {PhiConsentApprovalView} from './phi-consent-approval-view';
import HttpInterceptor from '../../../../shared/Http/http-interceptor';
import {NoSigAuthDocApprovalPage} from '../no-sig-auth-doc-approval-page';
import {
  AuthorizationDocumentStatus,
} from '../payment-program-enrollment/payment-program-enrollment-page';


export const PhiConsentPage = () => {

  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ token?: string }>();

  const [patientEncounter, setPatientEncounter] = useState<PatientEncounter | null>(
      null);
  const [msg, setMsg] = useState<ApplicationStatusState>(
      emptyApplicationStatus);
  const [guestToken, setGuestToken] = useState<GuestToken | null>(null);
  const [authDocStatus, setAuthDocStatus] = useState<AuthorizationDocumentStatus | null>(
      null);
  const [authDocPdfBlob, setAuthDocPdfBlob] = useState<Blob | null>(null);
  const [isAuthDocAlreadyApproved, setIsAuthDocAlreadyApproved] = useState(
      false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  useEffect(() => {

    const getTokenAndPhiConsent = async () => {

      /**
       * A temporary measure to log out user session before loading the page.
       * If a patient portal session exists, it interferes with the loading of all guest pages.
       * */
      await dispatch(logUserOut());

      const tokenResponse = await dispatch(getAnonymousRequestToken()).unwrap();

      setGuestToken(tokenResponse);
      HttpInterceptor(false, tokenResponse, true);

      const launchPayloadResponse: any = await dispatch(getLaunchPayload(({
        'encryptedValue': params.token as string,
        'authToken': tokenResponse?.token,
      }))).unwrap();

      if (launchPayloadResponse?.isProcessingError) {
        throw Error('Error decrypting launch payload');
      }

      const patientEncounterResponse = await paynowService.getPatientEncounter(
          +launchPayloadResponse?.patientEncounterId,
          tokenResponse?.token,
          {cacheMode: 'no-cache'},
      );

      const authDocStatusId = +launchPayloadResponse?.authDocStatusId;

      const selectedAuthDoc = patientEncounterResponse.authorizationDocumentStatus.find(
          (authDoc: AuthorizationDocumentStatus) => {
            if (authDocStatusId) {
              return authDoc.authorizationDocumentStatusId === authDocStatusId &&
                  authDoc.authorizationDocuments.typeOfDoc === 'phi' &&
                  authDoc.documentStatus !== 'signed';
            }

            return authDoc.authorizationDocuments.typeOfDoc === 'phi' &&
                authDoc.documentStatus !== 'signed';
          });

      if (!selectedAuthDoc) {
        setIsAuthDocAlreadyApproved(true);
        return;
      }

      const response = await paynowService.postApi(
          'GET',
          `/patient-encounter/v2/template/${selectedAuthDoc?.documentTypeId}/export`,
          null,
          tokenResponse?.token,
          'application/pdf',
      );
      if (response instanceof ArrayBuffer) {
        const blob = new Blob([response], {type: 'application/pdf'});
        setAuthDocPdfBlob(blob);
      }

      setPatientEncounter(patientEncounterResponse);
      setAuthDocStatus(selectedAuthDoc);
    };

    getTokenAndPhiConsent().then(() => {
      setIsPageLoading(false);
    }).catch((err) => {
      console.error(err);
      setIsPageLoading(false);
      setMsg(GetError(GENERIC_ERROR_MSG));
    });

  }, []);

  return (
      <NoSigAuthDocApprovalPage
          isPageLoading={isPageLoading}
          isAuthDocAlreadyApproved={isAuthDocAlreadyApproved}
          areRecordsReady={!!patientEncounter}
          approvalView={
            <PhiConsentApprovalView
                patientEncounter={patientEncounter!}
                selectedAuthDocStatus={authDocStatus}
                guestToken={guestToken}
                setMessage={(msg: ApplicationStatusState) => setMsg(msg)}
                authDocPdfBlob={authDocPdfBlob}
            />
          }
          errorMsg={GENERIC_ERROR_MSG}
          msgState={[msg, setMsg]}
          alreadyApprovedText={'This PHI consent has already been approved.'}
      />
  );
};


const GENERIC_ERROR_MSG = 'There is something wrong with your PHI consent. If the problem persists, please reach out to a FinPay administrator at support@finpay.com.';
